import { useCallback, useContext, useEffect, useReducer, useRef } from "react";

import {
    CommentsApi,
    CommentContentType,
    getVideoCommentErrorCode,
    getVideoCommentErrorStatus,
    isVideoCommentRequestCancelled,
} from "../../Api/CommentsAPI";
import { UserAuthenticationContext } from "../../Context/UserAuthenticationContext";
import {
    SubmitVideoCommentReportRequestDTO,
    VideoCommentDTO,
    VideoCommentSort,
} from "../../Models/DTOs/VideoCommentsDTO";
import { BLOCKED_COMMENT_MESSAGE } from "../../Helpers/Comments";
import {
    initialVideoCommentsState,
    videoCommentsReducer,
} from "./Video/videoCommentsState";

const ROOT_PAGE_SIZE = 20;
const REPLY_PAGE_SIZE = 20;

export class VideoCommentClientError extends Error {
    code: string;

    constructor(code: string) {
        super(code);
        this.name = "VideoCommentClientError";
        this.code = code;
    }
}

export const videoCommentErrorCode = (error: unknown): string => {
    if (error instanceof VideoCommentClientError) return error.code;
    return getVideoCommentErrorCode(error) ?? "comments_unavailable";
};

export const describeVideoCommentError = (error: unknown, contentType: CommentContentType = "video"): string => {
    const code = videoCommentErrorCode(error);
    const messages: Record<string, string> = {
        article_not_found: "This article is no longer available.",
        video_not_found: "This video is no longer available.",
        authentication_required: "Please sign in to continue.",
        active_subscription_required:
            "An active membership is required to comment and vote.",
        comment_body_required: "Enter a comment before submitting.",
        comment_body_too_long: "Comments can be no longer than 2,000 characters.",
        comment_body_contains_control_characters:
            "The comment contains unsupported characters.",
        comment_body_contains_blocked_word: BLOCKED_COMMENT_MESSAGE,
        comment_body_reserved: "That text is reserved and cannot be posted.",
        parent_comment_deleted: "That comment was deleted and can no longer receive replies.",
        parent_comment_not_found: "That comment is no longer available.",
        comment_deleted: "That comment has been deleted.",
        comment_not_found: "That comment is no longer available.",
        comment_version_conflict:
            "This comment changed while you were working. The latest version has been loaded.",
        comment_report_already_resolved:
            "Another moderator already resolved this report. The queue has been refreshed.",
        comment_report_not_found: "That report is no longer available.",
        report_resolution_required: "Enter a resolution before continuing.",
        report_resolution_too_long:
            "Resolutions can be no longer than 1,000 characters.",
        comment_not_pinnable: "Only a visible root comment can be pinned.",
        comment_pin_forbidden:
            `You can only manage pinned comments on ${contentType === "article" ? "articles" : "videos"} assigned to your author profile.`,
        comment_report_already_open:
            "You already have an open report for this comment.",
        comment_report_forbidden: "You cannot report your own comment.",
        report_details_too_long: "Report details can be no longer than 1,000 characters.",
        comment_permissions_unavailable:
            "Comment permissions are temporarily unavailable. Please try again.",
        invalid_access_token: "Your session expired. Please sign in again.",
        not_admin: "Moderator access is required.",
    };
    return messages[code] ?? "Something went wrong. Please try again.";
};

type TokenOperation<T> = (
    accessToken: string,
    abortController: AbortController
) => Promise<T>;
type OptionalTokenOperation<T> = (
    accessToken: string | null,
    abortController: AbortController
) => Promise<T>;

export function useContentComments(
    contentId: number | undefined,
    api: CommentsApi,
    contentType: CommentContentType
) {
    const auth = useContext(UserAuthenticationContext);
    const authRef = useRef(auth);
    authRef.current = auth;

    const [state, dispatch] = useReducer(
        videoCommentsReducer,
        initialVideoCommentsState
    );
    const stateRef = useRef(state);
    stateRef.current = state;

    const mountedRef = useRef(true);
    const controllersRef = useRef<Set<AbortController>>(new Set());
    const rootsControllerRef = useRef<AbortController | null>(null);
    const threadControllersRef = useRef<Record<number, AbortController>>({});
    const requestScopeRef = useRef<string>("");

    const registerController = useCallback(() => {
        const controller = new AbortController();
        controllersRef.current.add(controller);
        return controller;
    }, []);

    const releaseController = useCallback((controller: AbortController) => {
        controllersRef.current.delete(controller);
    }, []);

    const withOptionalAuth = useCallback(
        async <T,>(
            operation: OptionalTokenOperation<T>,
            controller: AbortController
        ): Promise<T> => {
            const currentAuth = authRef.current;
            let token = currentAuth.doesAuthTokenExist()
                ? await currentAuth.getValidAccessToken()
                : null;

            try {
                return await operation(token, controller);
            } catch (error) {
                if (getVideoCommentErrorStatus(error) !== 401 || !token) throw error;

                const refreshed = await currentAuth.refreshTokens();
                const refreshedToken = refreshed
                    ? await currentAuth.getValidAccessToken()
                    : null;

                // A comments read is public. If the session cannot be recovered,
                // retry once anonymously rather than failing the whole discussion.
                return operation(refreshedToken, controller);
            }
        },
        []
    );

    const withRequiredAuth = useCallback(
        async <T,>(
            operation: TokenOperation<T>,
            controller: AbortController
        ): Promise<T> => {
            const currentAuth = authRef.current;
            let token = await currentAuth.getValidAccessToken();
            if (!token) throw new VideoCommentClientError("authentication_required");

            try {
                return await operation(token, controller);
            } catch (error) {
                if (getVideoCommentErrorStatus(error) !== 401) throw error;

                const refreshed = await currentAuth.refreshTokens();
                token = refreshed ? await currentAuth.getValidAccessToken() : null;
                if (!token) throw error;
                return operation(token, controller);
            }
        },
        []
    );

    const requestRoots = useCallback(
        async (append: boolean, cursor: string | null = null): Promise<void> => {
            if (!contentId || contentId <= 0) return;

            if (!append) rootsControllerRef.current?.abort();
            const controller = registerController();
            rootsControllerRef.current = controller;
            dispatch({ type: "ROOTS_LOADING", append });

            try {
                const page = await withOptionalAuth(
                    (token, requestController) =>
                        api.getComments(
                            contentId,
                            stateRef.current.sort,
                            ROOT_PAGE_SIZE,
                            cursor,
                            token,
                            requestController
                        ),
                    controller
                );
                if (mountedRef.current && !controller.signal.aborted) {
                    dispatch({ type: "ROOTS_SUCCESS", page, append });
                }
            } catch (error) {
                if (
                    mountedRef.current &&
                    !controller.signal.aborted &&
                    !isVideoCommentRequestCancelled(error)
                ) {
                    dispatch({
                        type: "ROOTS_ERROR",
                        message: describeVideoCommentError(error, contentType),
                        append,
                    });
                }
            } finally {
                if (rootsControllerRef.current === controller) {
                    rootsControllerRef.current = null;
                }
                releaseController(controller);
            }
        },
        [api, contentType, registerController, releaseController, contentId, withOptionalAuth]
    );

    const requestReplies = useCallback(
        async (
            rootId: number,
            append: boolean,
            cursor: string | null = null
        ): Promise<void> => {
            if (!rootId) return;
            if (!append) threadControllersRef.current[rootId]?.abort();

            const controller = registerController();
            threadControllersRef.current[rootId] = controller;
            dispatch({ type: "THREAD_LOADING", rootId, append });

            try {
                const page = await withOptionalAuth(
                    (token, requestController) =>
                        api.getReplies(
                            rootId,
                            REPLY_PAGE_SIZE,
                            cursor,
                            token,
                            requestController
                        ),
                    controller
                );
                if (mountedRef.current && !controller.signal.aborted) {
                    dispatch({ type: "THREAD_SUCCESS", rootId, page, append });
                }
            } catch (error) {
                if (
                    mountedRef.current &&
                    !controller.signal.aborted &&
                    !isVideoCommentRequestCancelled(error)
                ) {
                    dispatch({
                        type: "THREAD_ERROR",
                        rootId,
                        message: describeVideoCommentError(error, contentType),
                        append,
                    });
                }
            } finally {
                if (threadControllersRef.current[rootId] === controller) {
                    delete threadControllersRef.current[rootId];
                }
                releaseController(controller);
            }
        },
        [api, contentType, registerController, releaseController, withOptionalAuth]
    );

    const refreshRoots = useCallback(
        () => requestRoots(false, null),
        [requestRoots]
    );

    const loadMoreRoots = useCallback(() => {
        const current = stateRef.current;
        if (current.loadingMore || !current.nextCursor) return Promise.resolve();
        return requestRoots(true, current.nextCursor);
    }, [requestRoots]);

    const ensureReplies = useCallback(
        (rootId: number) => {
            const thread = stateRef.current.threads[rootId];
            if (thread?.loaded || thread?.loading) return Promise.resolve();
            return requestReplies(rootId, false, null);
        },
        [requestReplies]
    );

    const refreshReplies = useCallback(
        (rootId: number) => requestReplies(rootId, false, null),
        [requestReplies]
    );

    const loadMoreReplies = useCallback(
        (rootId: number) => {
            const thread = stateRef.current.threads[rootId];
            if (!thread?.nextCursor || thread.loadingMore) return Promise.resolve();
            return requestReplies(rootId, true, thread.nextCursor);
        },
        [requestReplies]
    );

    const findComment = useCallback((commentId: number): VideoCommentDTO | null => {
        const current = stateRef.current;
        if (current.pinnedComment?.id === commentId) return current.pinnedComment;
        const root = current.items.find((comment) => comment.id === commentId);
        if (root) return root;
        for (const thread of Object.values(current.threads)) {
            const reply = thread.items.find((comment) => comment.id === commentId);
            if (reply) return reply;
        }
        return null;
    }, []);

    const reconcileComment = useCallback(
        async (commentId: number): Promise<void> => {
            const comment = findComment(commentId);
            const rootId = comment?.root_comment_id ?? comment?.id ?? commentId;
            await requestRoots(false, null);
            if (stateRef.current.threads[rootId]?.loaded) {
                await requestReplies(rootId, false, null);
            }
        },
        [findComment, requestReplies, requestRoots]
    );

    const runMutation = useCallback(
        async <T,>(operation: TokenOperation<T>): Promise<T> => {
            const controller = registerController();
            try {
                const result = await withRequiredAuth(operation, controller);
                if (controller.signal.aborted || !mountedRef.current) {
                    throw new VideoCommentClientError("request_cancelled");
                }
                return result;
            } finally {
                releaseController(controller);
            }
        },
        [registerController, releaseController, withRequiredAuth]
    );

    const createComment = useCallback(
        async (
            body: string,
            idempotencyKey: string,
            parentCommentId?: number
        ): Promise<VideoCommentDTO> => {
            if (!contentId) throw new VideoCommentClientError(contentType === "article" ? "article_not_found" : "video_not_found");
            const comment = await runMutation((token, controller) =>
                api.createComment(
                    contentId,
                    {
                        body,
                        ...(parentCommentId
                            ? { parent_comment_id: parentCommentId }
                            : {}),
                    },
                    idempotencyKey,
                    token,
                    controller
                )
            );
            if (parentCommentId) {
                dispatch({ type: "REPLY_CREATED", comment });
            } else {
                await requestRoots(false, null);
            }
            return comment;
        },
        [api, contentType, requestRoots, runMutation, contentId]
    );

    const editComment = useCallback(
        async (commentId: number, body: string, version: string) => {
            try {
                const comment = await runMutation((token, controller) =>
                    api.editComment(
                        commentId,
                        { body, version },
                        token,
                        controller
                    )
                );
                dispatch({ type: "COMMENT_UPDATED", comment });
                return comment;
            } catch (error) {
                if (getVideoCommentErrorCode(error) === "comment_version_conflict") {
                    await reconcileComment(commentId);
                }
                throw error;
            }
        },
        [api, reconcileComment, runMutation]
    );

    const deleteComment = useCallback(
        async (commentId: number, version: string, reason?: string) => {
            try {
                await runMutation((token, controller) =>
                    api.deleteComment(
                        commentId,
                        { version, ...(reason ? { reason } : {}) },
                        token,
                        controller
                    )
                );
                await reconcileComment(commentId);
            } catch (error) {
                if (getVideoCommentErrorCode(error) === "comment_version_conflict") {
                    await reconcileComment(commentId);
                }
                throw error;
            }
        },
        [api, reconcileComment, runMutation]
    );

    const setVote = useCallback(
        async (commentId: number, vote: 1 | -1 | null) => {
            const cachedComment = findComment(commentId);
            const result = await runMutation((token, controller) =>
                vote === null
                    ? api.removeVote(commentId, token, controller)
                    : api.setVote(commentId, vote, token, controller)
            );
            dispatch({ type: "VOTE_UPDATED", vote: result });
            if (
                cachedComment?.root_comment_id === null &&
                stateRef.current.sort !== "newest"
            ) {
                await requestRoots(false, null);
            }
            return result;
        },
        [api, findComment, requestRoots, runMutation]
    );

    const reportComment = useCallback(
        async (
            commentId: number,
            request: SubmitVideoCommentReportRequestDTO
        ) => {
            const report = await runMutation((token, controller) =>
                api.reportComment(commentId, request, token, controller)
            );
            dispatch({ type: "COMMENT_REPORTED", commentId });
            return report;
        },
        [api, runMutation]
    );

    const pinComment = useCallback(
        async (commentId: number) => {
            if (!contentId) throw new VideoCommentClientError(contentType === "article" ? "article_not_found" : "video_not_found");
            const result = await runMutation((token, controller) =>
                api.pinComment(contentId, commentId, token, controller)
            );
            await requestRoots(false, null);
            return result;
        },
        [api, contentType, requestRoots, runMutation, contentId]
    );

    const unpinComment = useCallback(
        async (commentId: number) => {
            if (!contentId) throw new VideoCommentClientError(contentType === "article" ? "article_not_found" : "video_not_found");
            await runMutation((token, controller) =>
                api.unpinComment(contentId, commentId, token, controller)
            );
            await requestRoots(false, null);
        },
        [api, contentType, requestRoots, runMutation, contentId]
    );

    const setSort = useCallback((sort: VideoCommentSort) => {
        if (sort === stateRef.current.sort) return;
        Object.values(threadControllersRef.current).forEach((controller) =>
            controller.abort()
        );
        threadControllersRef.current = {};
        dispatch({ type: "SET_SORT", sort });
    }, []);

    const authIdentity = auth.userData?.id ?? "";
    const authAccessScope = [
        authIdentity,
        auth.userData?.admin_role_id ?? 0,
        auth.userData?.subscription?.status ?? "",
        auth.userData?.subscription?.plan_id ?? "",
        auth.userData?.subscription?.plan_key ?? "",
        auth.userData?.subscription?.is_entitled ? "1" : "0",
    ].join(":");
    useEffect(() => {
        if (!contentId || auth.authInitializing) return;
        const requestScope = `${contentType}:${contentId}:${authAccessScope}`;
        if (requestScopeRef.current !== requestScope) {
            requestScopeRef.current = requestScope;
            controllersRef.current.forEach((controller) => controller.abort());
            controllersRef.current.clear();
            rootsControllerRef.current = null;
            threadControllersRef.current = {};
            dispatch({ type: "RESET" });
        }
        void requestRoots(false, null);
    }, [auth.authInitializing, authAccessScope, contentType, requestRoots, state.sort, contentId]);

    useEffect(() => {
        mountedRef.current = true;
        const activeControllers = controllersRef.current;
        return () => {
            mountedRef.current = false;
            activeControllers.forEach((controller) => controller.abort());
            activeControllers.clear();
        };
    }, []);

    return {
        state,
        contentType,
        isModerator: (auth.userData?.admin_role_id ?? 0) >= 95,
        isAuthenticated: Boolean(auth.userData?.id),
        setSort,
        refreshRoots,
        loadMoreRoots,
        ensureReplies,
        refreshReplies,
        loadMoreReplies,
        createComment,
        editComment,
        deleteComment,
        setVote,
        reportComment,
        pinComment,
        unpinComment,
    };
}

export type CommentsController = ReturnType<typeof useContentComments>;
